/* eslint-disable complexity */
import { Badge, Body, Button, buttonVariants, Title } from '@e2n/ui/v2';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { Abschnitt as AbschnittType } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import {
  faCalendarRange,
  faChevronDown,
  faChevronUp,
  // faFileLines,
  faPen,
  faPlus,
  faUser,
  faStar as faStarEmpty,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { faCircle, faStar } from '@fortawesome/pro-solid-svg-icons';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { StationContainer } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/StationContainer';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { DeleteAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteAbschnitt/DeleteAbschnitt.tsx';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan.ts';
import { AbwesenheitenPopover } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/AbwesenheitenPopover';
import { KonfliktIndicator } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/KonfliktIndicator';
import { ErinnerungenInfo } from '@/domain/mitarbeiter/features/rotationsplan/components/ErinnerungenInfo';

/* eslint-disable max-lines-per-function */
export function Abschnitt({
  abschnitt,
  rotationsplan,
}: {
  abschnitt: AbschnittType;
  rotationsplan: Rotationsplan;
}) {
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  function renderBewertungAction(
    station: ArbeitsbereichStation,
    isMobile: boolean,
  ) {
    if (station.bewertung) {
      return (
        <Button asChild size="small" variant={isMobile ? 'outline' : 'ghost'}>
          <Link
            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/bewertung/${station.bewertung.id}`}>
            <FontAwesomeIcon
              icon={station.bewertung.sterne !== 0 ? faStar : faStarEmpty}
            />
            {station.bewertung.sterne !== 0
              ? `${station.bewertung.sterne} / 5`
              : '--'}
          </Link>
        </Button>
      );
    }

    if (station.istAktiv || station.istAbgeschlossen) {
      return (
        <Button asChild variant="secondary-blue">
          <Link
            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/bewertung/create`}>
            {t('domain.mitarbeiter.rotationsplan.rotationsplaene.bewerten')}
          </Link>
        </Button>
      );
    }

    return null;
  }

  return (
    <div key={abschnitt.id} className="flex flex-col gap-5">
      <div className="flex items-center gap-2 p-1 md:hover:bg-neutrals-v2-200 hover:rounded-lg group">
        <Button
          iconOnly
          variant="ghost-on-grey"
          onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </Button>
        <Title>{abschnitt.position + '. ' + abschnitt.name}</Title>
        <div className="block ml-auto md:hidden md:group-hover:block md:ml-0">
          <DeleteAbschnitt
            abschnitt={abschnitt}
            rotationsplan={rotationsplan}
          />
        </div>
      </div>
      <div className="flex flex-col">
        {abschnitt.stationen
          .toSorted((stationA, stationB) => {
            if (stationA.von < stationB.von) return -1;
            if (stationA.von > stationB.von) return 1;
            return 0;
          })
          .map((station) => {
            const abwesenheiten = rotationsplan.abwesenheitenByStationId(
              station.id,
            );
            const konfliktStationen =
              station instanceof ArbeitsbereichStation
                ? rotationsplan.getUeberschneidendeStationen(station)
                : [];

            const borderClass =
              konfliktStationen.length > 0
                ? 'border-error-red-200'
                : station.istAktiv
                  ? 'border-brand-blue-100 shadow-lg'
                  : 'border-transparent';

            const abwesenheitenBadge = (
              <div className="flex items-center gap-1">
                <Badge className="bg-neutrals-v2-200 text-labels-primary">
                  {abwesenheiten.length > 1
                    ? t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.abwesenheiten_other',
                        { count: abwesenheiten.length },
                      )
                    : t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.abwesenheiten_one',
                        { count: abwesenheiten.length },
                      )}
                </Badge>
                <AbwesenheitenPopover abwesenheiten={abwesenheiten} />
              </div>
            );

            return station instanceof ArbeitsbereichStation ? (
              isOpen && (
                <StationContainer station={station} key={station.id}>
                  <div
                    className={`mb-4 w-full min-w-0 bg-backgrounds-primary rounded-xl border-2 flex flex-col gap-3 p-3.5 ${borderClass}`}>
                    <div className="flex justify-between items-start gap-8">
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={faCircle}
                              color={station.arbeitsbereich.getBorderColor()}
                              className="size-2.5"
                            />
                            <Title>{station.arbeitsbereich.name}</Title>
                          </div>
                          {abwesenheiten.length > 0 && (
                            <div className="hidden md:flex">
                              {abwesenheitenBadge}
                            </div>
                          )}
                        </div>
                        {abwesenheiten.length > 0 && (
                          <div className="flex md:hidden">
                            {abwesenheitenBadge}
                          </div>
                        )}
                        {station.lernziele && station.lernziele.length > 0 && (
                          <Body className="text-labels-secondary line-clamp-3">
                            {station.lernziele}
                          </Body>
                        )}
                      </div>
                      <div className="flex flex-row items-center gap-2 shrink-0">
                        <div className="hidden md:flex items-center gap-2">
                          {renderBewertungAction(station, false)}
                        </div>
                        <Button asChild iconOnly variant="secondary-red">
                          <Link
                            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/update`}>
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-labels-secondary">
                      <div className="flex items-center gap-1">
                        <FontAwesomeIcon
                          className="size-3.5"
                          icon={faCalendarRange}
                        />
                        <Body className="text-labels-secondary" size="sm">
                          {dayjs(station.von).format('DD.MM.YYYY') +
                            ' – ' +
                            dayjs(station.bis).format('DD.MM.YYYY')}
                        </Body>
                      </div>
                      {station.abteilungsleiter && (
                        <div className="flex items-center gap-1">
                          <FontAwesomeIcon className="size-3.5" icon={faUser} />
                          <Body className="text-labels-secondary" size="sm">
                            {station.abteilungsleiter.getName()}
                          </Body>
                        </div>
                      )}
                      <Location
                        className="text-labels-secondary"
                        standort={station.arbeitsbereich.standort}
                      />
                      {station.frist && station.frist.erinnerungenAm.length && (
                        <ErinnerungenInfo
                          className="text-labels-secondary"
                          frist={station.von}
                          erinnerungenAm={station.frist.erinnerungenAm}
                        />
                      )}
                    </div>
                    {konfliktStationen.length > 0 && (
                      <div className="flex">
                        <KonfliktIndicator
                          konfliktStationen={konfliktStationen}
                        />
                      </div>
                    )}
                    {(station.bewertung ||
                      station.istAktiv ||
                      station.istAbgeschlossen) && (
                      <div className="flex flex-col md:hidden">
                        {renderBewertungAction(station, true)}
                      </div>
                    )}
                  </div>
                </StationContainer>
              )
            ) : (
              <div key={station.id}>Wird noch nicht angezeigt</div>
            );
          })}
        {isOpen && (
          <StationContainer>
            <Link
              to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/create`}
              className="group/create flex items-center gap-2 w-full p-4 bg-backgrounds-primary rounded-xl">
              <span
                className={buttonVariants({
                  variant: 'secondary-red',
                  iconOnly: true,
                  className: 'group-hover/create:bg-neutrals-v2-300',
                })}>
                <FontAwesomeIcon icon={faPlus} />
              </span>
              <Body>
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.label',
                )}
              </Body>
            </Link>
          </StationContainer>
        )}
      </div>
    </div>
  );
}
